import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from '../styles/StaticPages.module.css';

const FAQPage = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqs = [
    {
      question: "How does the custom song process work?",
      answer: "Our process consists of four simple steps: Package Selection: Choose from our Basic, Premium, or Deluxe packages. Information Submission: Complete our detailed questionnaire about your song requirements. Creation Process: Our team creates your custom song within 24–48 hours. Delivery & Review: Download your song and request any included revisions within 48 hours. Each step includes progress notifications via email to keep you informed throughout the process."
    },
    {
      question: "How long does it take to create my custom song?",
      answer: "Standard delivery takes approximately 24–48 hours from order confirmation. Rush delivery is available (3–6 hours) for an additional fee."
    },
    {
      question: "What information do I need to provide?",
      answer: "We'll ask for details about the recipient (name, personality traits, interests), the occasion, your relationship to them, specific memories or stories you'd like included, and your preferred music genres. The more information you provide, the more personalized your song will be."
    },
    {
      question: "Who will be singing my custom song?",
      answer: "Each song features vocals crafted to perfectly match your chosen genre and style. We utilize advanced production techniques to create professional-quality vocal performances tailored to your specific requirements."
    },
    {
      question: "Can I request revisions to my song?",
      answer: "Yes, if your package includes revisions. The Premium package includes 1 revision. The Deluxe package includes 2 revisions. Basic packages do not include revisions. Revisions must be requested within 30 days of delivery."
    },
    {
      question: "What file format will my song be delivered in?",
      answer: "Your custom song will be delivered as a high-quality MP3 file (320 kbps). If you need additional formats (such as WAV or FLAC), please mention this in your order notes or contact our support team."
    },
    {
      question: "Do I own the rights to my custom song?",
      answer: "You receive full personal-use rights to your custom song. You can play it at events, share it with friends and family, and use it in personal videos. Commercial rights (for advertising, resale, or public distribution) are not included in standard packages but can be purchased separately."
    },
    {
      question: "Can I request a specific genre or style?",
      answer: "Absolutely. We create songs in a wide variety of genres including pop, rock, country, R&B, electronic, folk, jazz, classical, and more. You can specify your preferred genre(s) when placing your order."
    },
    {
      question: "How do I share my song with others?",
      answer: "After your song is delivered, you can download the MP3 file and share it however you like – email it to friends, play it at events, share it on social media, or burn it to a CD. For larger files or multiple formats, we provide a secure download link that remains active for 30 days."
    },
    {
      question: "What if I'm not satisfied with my song?",
      answer: "Your satisfaction is our priority. If you're not completely happy with your song, please use your included revision requests to let us know what changes you'd like. If you still have concerns after revisions, please contact our support team at support@mail.makemesongs.com and we will do our best to address your needs."
    },
    {
      question: "Can I include specific lyrics or phrases in my song?",
      answer: "Yes, you can request specific lyrics or references. While we'll do our best to include them, we'll also ensure the overall flow and quality of the song. When providing your song details, you can include specific phrases, inside jokes, or meaningful quotes you'd like incorporated into the lyrics."
    },
    {
      question: "How detailed should my song request be?",
      answer: "The more details you provide, the more personalized your song will be. Include information about the recipient's personality, your relationship, special memories, and any specific references you'd like included. However, don't worry if you're not sure what to say – our questionnaire will guide you through the process."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover), PayPal, and Apple Pay for secure online payments. All transactions are processed through our secure payment gateway to ensure your information is protected."
    },
    {
      question: "Is my personal information secure?",
      answer: "Absolutely. We take data security seriously and use industry-standard encryption and security measures to protect your personal information. We never share your details with third parties without your consent. For more information, please review our Privacy Policy."
    },
    {
      question: "What are your different package options?",
      answer: "We offer three service tiers: • Basic Package: One custom song, standard delivery, MP3 format • Premium Package: One custom song, 1 revision, choice of formats, priority delivery • Deluxe Package: One custom song, 2 revisions, all formats, rush delivery option, commercial rights available"
    },
    {
      question: "What happens after I submit my order?",
      answer: "After order confirmation, you'll receive: Immediate confirmation email with order details, creation timeline within 2 hours, progress updates at key stages, delivery notification with download instructions, and follow-up support for any questions or revision requests."
    },
    {
      question: "Do you offer subscriptions or bundles?",
      answer: "We currently offer single-song packages, but we're exploring bundles for weddings, holidays, or corporate clients. Contact us to discuss custom orders."
    },
    {
      question: "How long will my song download link stay active?",
      answer: "Your song will be available to download for 30 days. We recommend saving a backup once downloaded."
    },
    {
      question: "Will you share my song publicly?",
      answer: "We may feature select songs (anonymized or with permission) in our portfolio. Let us know at checkout if you prefer your song to remain private."
    }
  ];

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Frequently Asked Questions | Custom Song Service | MakeMeSongs</title>
        <meta name="description" content="Get answers about our personalized song creation process, delivery times, revisions, file formats, and more. Learn how we transform your stories into custom songs." />
        <meta name="keywords" content="custom song FAQ, personalized music questions, custom song delivery, song revisions, music gift questions" />
        <meta property="og:title" content="Frequently Asked Questions | MakeMeSongs" />
        <meta property="og:description" content="Find answers about our personalized song creation service - from how the process works to delivery times, file formats, and rights. Create your perfect musical gift." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://makemesongs.com/faq" />
        <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
        <link rel="canonical" href="https://makemesongs.com/faq" />
      </Helmet>
      {/* Move the title and intro text outside the contentWrapper */}
      <h1 className={styles.pageTitle}>Frequently Asked Questions</h1>
      <p className={styles.introText}>
        Find answers to the most common questions about our custom song creation service.
        If you don't see your question answered here, please <a href="mailto:support@mail.makemesongs.com" className={styles.inlineLink}>contact us</a>.
      </p>

      <div className={styles.contentWrapper}>
        <div className={styles.faqContainer}>
          {faqs.map((faq, index) => (
            <div 
              key={index} 
              className={`${styles.faqItem} ${activeIndex === index ? styles.faqItemActive : ''}`}
            >
              <div 
                className={styles.faqQuestion}
                onClick={() => toggleFAQ(index)}
              >
                <h3>{faq.question}</h3>
                <span className={styles.faqToggle}>
                  {activeIndex === index ? '−' : '+'}
                </span>
              </div>
              {activeIndex === index && (
                <div className={styles.faqAnswer}>
                  <p>
                    {faq.answer.includes("Terms of Service") ? (
                      faq.answer.split("Terms of Service").map((part, i, arr) => 
                        i < arr.length - 1 ? 
                          <React.Fragment key={i}>{part}<Link to="/terms-privacy#terms" className={styles.inlineLink}>Terms of Service</Link></React.Fragment> : 
                          part
                      )
                    ) : faq.answer.includes("Privacy Policy") ? (
                      faq.answer.split("Privacy Policy").map((part, i, arr) => 
                        i < arr.length - 1 ? 
                          <React.Fragment key={i}>{part}<Link to="/terms-privacy#privacy" className={styles.inlineLink}>Privacy Policy</Link></React.Fragment> : 
                          part
                      )
                    ) : faq.answer.includes("Refund Policy") ? (
                      faq.answer.split("Refund Policy").map((part, i, arr) => 
                        i < arr.length - 1 ? 
                          <React.Fragment key={i}>{part}<Link to="/terms-privacy#refund" className={styles.inlineLink}>Refund Policy</Link></React.Fragment> : 
                          part
                      )
                    ) : (
                      faq.answer
                    )}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
        
        <div className={styles.additionalHelp}>
          <h2>Still have questions?</h2>
          <p>Our team is here to help you create the perfect personalized song.</p>
          <div className={styles.helpButtons}>
            <a href="/contact" className={styles.primaryButton}>Contact Support</a>
            <a href="/packages" className={styles.secondaryButton}>View Our Packages</a>
          </div>
          <p className={styles.legalLinks}>
            Review our <Link to="/terms-privacy#terms" className={styles.inlineLink}>Terms of Service</Link>, <Link to="/terms-privacy#privacy" className={styles.inlineLink}>Privacy Policy</Link>, and <Link to="/terms-privacy#refund" className={styles.inlineLink}>Refund Policy</Link> for more information.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;